import React, { useState, useEffect } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../api/cases';
import { getActiveQuestionnaires } from '../../auto-estimate/services/estimateApi';

const CaseCategoryManager = ({ isOpen, onClose }) => {
    const [categories, setCategories] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(false);

    // Editing state: { id: number | 'new', label: string, code: string, order: number }
    const [editRowId, setEditRowId] = useState(null);
    const [editData, setEditData] = useState({});
    const [estimateVersions, setEstimateVersions] = useState([]);

    useEffect(() => {
        if (isOpen) {
            fetchCategories();
        }
    }, [isOpen]);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const [catData, versionsData] = await Promise.all([
                getCategories(),
                getActiveQuestionnaires()
            ]);
            setCategories(catData);
            setEstimateVersions(versionsData || []);
        } catch (error) {
            console.error("Failed to load categories", error);
        } finally {
            setLoading(false);
        }
    };

    // Start Editing
    const handleEdit = (category) => {
        setEditRowId(category.id);
        setEditData({ ...category });
    };

    // Add New Row
    const handleAddRow = () => {
        if (editRowId === 'new') return; // Already adding
        setEditRowId('new');
        setEditData({ label: '', code: '', order: categories.length + 1, relatedVersionId: null });
    };

    // Cancel Editing
    const handleCancel = () => {
        setEditRowId(null);
        setEditData({});
    };

    // Save Changes
    const handleSave = async () => {
        if (!editData.label || !editData.code) {
            alert('카테고리명과 코드는 필수입니다.');
            return;
        }

        try {
            // Description is removed from DB, so we don't send it.
            const payload = {
                label: editData.label,
                code: editData.code,
                order: editData.order,
                relatedVersionId: editData.relatedVersionId || null
            };

            if (editRowId === 'new') {
                await createCategory(payload);
            } else {
                await updateCategory(editRowId, payload);
            }
            await fetchCategories();
            handleCancel();
        } catch (error) {
            console.error("Failed to save category", error);
            alert("저장 실패");
        }
    };

    // Delete Category
    const handleDelete = async (id) => {
        if (!window.confirm("정말 삭제하시겠습니까? 관련 사례가 목록에서 보이지 않을 수 있습니다.")) return;
        try {
            await deleteCategory(id);
            await fetchCategories();
        } catch (error) {
            console.error("Failed to delete category", error);
            alert("삭제 실패");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '950px', width: '95%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3>사례 카테고리 관리</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>

                <div className="category-list-container" style={{ overflowX: 'auto' }}>
                    <table className="admin-table" style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>순서</th>
                                <th style={{ minWidth: '120px' }}>카테고리명</th>
                                <th style={{ minWidth: '100px' }}>코드 (영문)</th>
                                <th style={{ minWidth: '180px' }}>연결 견적서</th>
                                <th style={{ width: '120px' }}>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((cat) => (
                                <tr key={cat.id}>
                                    {editRowId === cat.id ? (
                                        <>
                                            <td><input type="number" value={editData.order} onChange={(e) => setEditData({ ...editData, order: parseInt(e.target.value) })} style={{ width: '50px' }} /></td>
                                            <td><input type="text" value={editData.label} onChange={(e) => setEditData({ ...editData, label: e.target.value })} /></td>
                                            <td><input type="text" value={editData.code} onChange={(e) => setEditData({ ...editData, code: e.target.value })} /></td>
                                            <td>
                                                <select
                                                    value={editData.relatedVersionId || ''}
                                                    onChange={(e) => setEditData({ ...editData, relatedVersionId: e.target.value ? parseInt(e.target.value) : null })}
                                                    style={{ width: '100%', padding: '4px' }}
                                                >
                                                    <option value="">연결 없음</option>
                                                    {estimateVersions.map(v => (
                                                        <option key={v.id} value={v.id}>{v.label || v.name}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <button onClick={handleSave} className="btn-small bg-blue" style={{ marginRight: '5px' }}>저장</button>
                                                <button onClick={handleCancel} className="btn-small">취소</button>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{cat.order}</td>
                                            <td>{cat.label}</td>
                                            <td>{cat.code}</td>
                                            <td style={{ fontSize: '0.85rem', color: '#666' }}>
                                                {cat.relatedVersionId
                                                    ? (estimateVersions.find(v => v.id === cat.relatedVersionId)?.label || `ID: ${cat.relatedVersionId}`)
                                                    : <span style={{ color: '#999' }}>-</span>
                                                }
                                            </td>
                                            <td>
                                                <button onClick={() => handleEdit(cat)} className="btn-small" style={{ marginRight: '5px' }}>수정</button>
                                                <button onClick={() => handleDelete(cat.id)} className="btn-small bg-red">삭제</button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            {/* New Row Input */}
                            {editRowId === 'new' && (
                                <tr>
                                    <td><input type="number" value={editData.order} onChange={(e) => setEditData({ ...editData, order: parseInt(e.target.value) })} style={{ width: '50px' }} /></td>
                                    <td><input type="text" value={editData.label} placeholder="카테고리명" onChange={(e) => setEditData({ ...editData, label: e.target.value })} /></td>
                                    <td><input type="text" value={editData.code} placeholder="CODE" onChange={(e) => setEditData({ ...editData, code: e.target.value })} /></td>
                                    <td>
                                        <select
                                            value={editData.relatedVersionId || ''}
                                            onChange={(e) => setEditData({ ...editData, relatedVersionId: e.target.value ? parseInt(e.target.value) : null })}
                                            style={{ width: '100%', padding: '4px' }}
                                        >
                                            <option value="">연결 없음</option>
                                            {estimateVersions.map(v => (
                                                <option key={v.id} value={v.id}>{v.label || v.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <button onClick={handleSave} className="btn-small bg-blue" style={{ marginRight: '5px' }}>저장</button>
                                        <button onClick={handleCancel} className="btn-small">취소</button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                    <button
                        onClick={handleAddRow}
                        className="btn-primary"
                        disabled={editRowId === 'new'}
                        style={{ width: '100%' }}
                    >
                        + 카테고리 추가
                    </button>
                </div>
            </div>
            <style>{`
                .category-list-container input {
                    width: 100%;
                    padding: 4px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                }
            `}</style>
        </div>
    );
};

export default CaseCategoryManager;
